import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import { UploadConsentModal } from '@/components/consents/uploadConsentModal';
import { usePermission } from '@/hooks/usePermission';
import { routes } from '@/types/routes';
import { Empty } from '@/components/ui/empty';
import { BoardPenIcon } from '@/assets';
import { ErrorComponent } from '@/components/generalError';
import { getConsentFormTemplatesQueryOptions } from '@/api/queries/consents';
import { ConsentTemplatesTable } from '@/components/consents/consentTemplatesTable';

export const Route = createFileRoute('/_app/consents/')({
  loader: ({ context: { queryClient } }) => {
    return queryClient.ensureQueryData(getConsentFormTemplatesQueryOptions());
  },
  component: RouteComponent,
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermission('consent_form.canCreateTemplate', { allowAnySite: true });
  if (!permissions.isLoading && !permissions.hasPermissions) {
    void navigate({ to: routes.patients, replace: true });
    return null;
  }

  const { data: { items } = { items: [] }, isFetching } = useSuspenseQuery(getConsentFormTemplatesQueryOptions());
  const isEmpty = items.length === 0;

  return (
    <div className="flex flex-col flex-1">
      <div className="flex justify-between items-center mb-space-2xl">
        <h1 className="text-3xl font-semibold">{ t('title', { ns: 'consents' }) }</h1>
        { !isEmpty && <UploadConsentModal /> }
      </div>

      { isEmpty
        ? (
          <>
            <Empty
              title={ t('emptyState.title', { ns: 'consents' }) }
              description={ t('emptyState.description', { ns: 'consents' }) }
              image={ BoardPenIcon }
              showImageWrapper
              className="pt-20 pb-10"
            />
            <div className="flex justify-center">
              <UploadConsentModal />
            </div>
          </>
        )
        : <ConsentTemplatesTable templates={ items } isLoading={ isFetching } /> }
    </div>
  );
}
